/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import keys from 'lodash/keys';
import values from 'lodash/values';
import {
    ProgressIndicator,
    SecurityHelper,
    CSRFacilityInformation,
    Heading,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import { SendGAEvent } from 'shared-ui/utils/gaCommon';
import { authModalActions } from 'shared-ui/auth/redux';

import { INVENTORY_VEHICLE_PERMIT } from 'ui-field-sales/src/constants/types';
import ErrorNotification from 'ui-permit/src/components/ErrorNotification';

import * as pActions from '../actions/permit';
import ReservationSidebar from '../components/registration/ReservationSidebar';
import { feeTypes } from '../utils/feeUtils';
import { USE_FEE, progressSteps } from '../constants';
import {
    ALL_DIVISIONS_MAPPING,
    ALL_PERMIT_TYPES_MAPPING,
    SALES_CURRENTLY_BLOCKED,
} from '../utils/utils';
import { readRuleAsBoolean } from '../utils/permitUtils';

const buttonLabels = { CONTINUE: 'Continue', LOADING: 'Loading' };

const propTypes = {
    router: COMPAT_ROUTER_PROP_TYPES,
    fetchError: PropTypes.object,
    permit: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.number,
        category: PropTypes.number,
        entrances: PropTypes.arrayOf(PropTypes.object),
        divisions: PropTypes.object,
        recareas: PropTypes.arrayOf(PropTypes.string),
        validations: PropTypes.arrayOf(PropTypes.object),
    }),
    pActions: PropTypes.object.isRequired,
    openLoginModal: PropTypes.func.isRequired,
    postError: PropTypes.object,
};

const defaultProps = {};

function PermitRegistrationPageHOC(QuestionsComponent, ResultsComponent) {
    class PermitRegistrationPage extends React.Component {
        constructor(props) {
            super(props);

            this.state = {
                results: [],
                formIsValid: false,
                buttonLabel: buttonLabels.CONTINUE,
                selection: {},
            };

            this.addToCart = this.addToCart.bind(this);
            this.permitSelectionChange = this.permitSelectionChange.bind(this);
        }

        componentDidMount() {
            // need to get permit first in case they click "x" on the login modal
            const permitId = this.props.router.params.permitId;
            this.props.pActions.fetchPermit(permitId).then(() => {});
            this.setState({ buttonLabel: buttonLabels.CONTINUE });

            const loggedin = SecurityHelper.isLoggedIn();
            if (!loggedin) {
                this.props.openLoginModal();
                return;
            }

            this.props.pActions.purgePendingReservations();
        }

        addToCart() {
            this.setState({ formIsValid: false, buttonLabel: buttonLabels.LOADING });
            const { selection } = this.state;
            const items = keys(selection)
                .filter((k) => selection[k])
                .map((k) => ({
                    permit_type: ALL_PERMIT_TYPES_MAPPING[k],
                    division_id: ALL_DIVISIONS_MAPPING[k],
                    permit_id: this.props.permit.id,
                }));
            this.props.pActions.createPendingReservations(items);
            this.props.router.navigate(
                `/vehiclepermits/${this.props.permit.id}/order/detail`
            );
        }

        permitSelectionChange(selection) {
            const results = [];
            let div;
            keys(selection).forEach((key) => {
                if (selection[key]) {
                    div = this.props.permit.divisions[ALL_DIVISIONS_MAPPING[key]];
                    const fee = div.fees.find((f) => f.type === feeTypes.get(USE_FEE));
                    if (fee && div) {
                        results.push({
                            permitDisplayName: div.name,
                            feeDisplayLabel: `${fee.description}`,
                        });
                    }
                }
            });
            // it should not find any unanswered questions
            const formIsValid =
                values(selection).indexOf(null) === -1 && results.length > 0;
            this.setState({ results, formIsValid, selection });
        }

        render() {
            const { results, formIsValid, buttonLabel } = this.state;
            let resultsComponent = null;
            if (results.length > 0) {
                resultsComponent = <ResultsComponent results={results} />;
            }
            const errors = [];
            if (this.props.fetchError) {
                errors.push(this.props.fetchError);
            }
            if (this.props.postError) {
                errors.push(this.props.postError);
            }

            const isSalesEnabled = readRuleAsBoolean('IsEnabled', this.props.permit);
            if (this.props.permit.id && !isSalesEnabled) {
                errors.push(SALES_CURRENTLY_BLOCKED);
            }
            return (
                <div className="vehicle-permit registration-page">
                    {/* Fire the view_item GA Event */}
                    <SendGAEvent
                        eventType="view_item"
                        itemLevel="Registration Detail"
                        itemCategory={INVENTORY_VEHICLE_PERMIT}
                        facilityId={this.props.permit.id}
                        facilityName={this.props.permit.name}
                    />
                    <div className="rec-page">
                        <section className="rec-progress-summary">
                            <div className="rec-progress-bar-outer-wrap">
                                <div className="rec-progress-bar-wrap">
                                    <ProgressIndicator
                                        steps={progressSteps}
                                        activeStep={1}
                                    />
                                </div>
                            </div>
                        </section>
                        <div className="permit-content-outer-wrap">
                            <div className="rec-nested-wrap">
                                <section className="permit-content-main">
                                    <Heading appearance="h3" headingLevel={1}>
                                        Order Details
                                    </Heading>
                                    <ErrorNotification
                                        className="permit-error-wrap"
                                        errors={errors}
                                    />
                                    <CSRFacilityInformation
                                        inventory="Permit"
                                        facility_id={this.props.permit.id}
                                    />
                                    <p>
                                        Answer the following questions to find the best
                                        permits you need to reserve for your trip.
                                    </p>
                                    <hr />

                                    <Heading
                                        appearance="h2"
                                        headingLevel={2}
                                        className="mb-1"
                                    >
                                        Permit Decision Questions
                                    </Heading>
                                    <p className="mb-3">
                                        For keyboard users, use the Tab key to change
                                        questions and the Arrow keys to select an answer.
                                    </p>
                                    <form id="form-name">
                                        <fieldset>
                                            <QuestionsComponent
                                                permitSelectionChange={
                                                    this.permitSelectionChange
                                                }
                                            />
                                        </fieldset>
                                    </form>
                                    {resultsComponent}
                                </section>

                                <div className="">
                                    <ReservationSidebar
                                        pActions={this.props.pActions}
                                        permit={this.props.permit}
                                        disableButtons={!formIsValid || !isSalesEnabled}
                                        buttonLabel={buttonLabel}
                                        addToCart={this.addToCart}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    }

    PermitRegistrationPage.displayName = 'PermitRegistrationPage';
    PermitRegistrationPage.propTypes = propTypes;
    PermitRegistrationPage.defaultProps = defaultProps;

    function mapDispatchToProps(dispatch) {
        return {
            pActions: bindActionCreators(pActions, dispatch),
            openLoginModal: bindActionCreators(authModalActions.openLoginModal, dispatch),
        };
    }

    function select(state) {
        return {
            permit: state.vehiclepermit.permit.result,
            fetchError: state.vehiclepermit.permit.fetch_err,
            postError: state.vehiclepermit.pendingReservations.err,
        };
    }

    return withCompatRouter(connect(select, mapDispatchToProps)(PermitRegistrationPage));
}

export default PermitRegistrationPageHOC;

// cSpell:ignore recareas, loggedin, vehiclepermits, vehiclepermit
